import React, { useCallback, useEffect, useState } from 'react'
import { CalendarDays, Plus, StickyNote, Trash2, UserRound } from 'lucide-react'
import http from '../../config/http'

const today = () => {
  const date = new Date()
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
}

export default function NotesPanel({ entityType, entityId }) {
  const [notes, setNotes] = useState([])
  const [form, setForm] = useState({ date: today(), title: '', content: '' })
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const loadNotes = useCallback(async () => {
    if (!entityId) return
    try {
      setLoading(true)
      const response = await http.get('/api/notes', { params: { entityType, entityId } })
      setNotes(response.data)
      setError('')
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load notes')
    } finally { setLoading(false) }
  }, [entityId, entityType])

  useEffect(() => { loadNotes() }, [loadNotes])

  const saveNote = async event => {
    event.preventDefault()
    if (!form.title.trim() || !form.content.trim()) return setError('Title and content are required')
    try {
      setSaving(true)
      const response = await http.post('/api/notes', { entityType, entityId, ...form })
      setNotes(previous => [response.data, ...previous])
      setForm({ date: today(), title: '', content: '' })
      setShowForm(false)
      setError('')
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save note')
    } finally { setSaving(false) }
  }

  const deleteNote = async noteId => {
    if (!window.confirm('Delete this note?')) return
    try {
      await http.delete(`/api/notes/${noteId}`)
      setNotes(previous => previous.filter(note => note._id !== noteId))
    } catch (err) { setError(err.response?.data?.message || 'Unable to delete note') }
  }

  return <div className="space-y-4">
    <div className="flex items-center justify-between gap-3">
      <div><h3 className="font-semibold text-gray-900">Notes</h3><p className="text-sm text-gray-500">A dated history of updates and conversations.</p></div>
      <button onClick={() => setShowForm(value => !value)} className="inline-flex items-center gap-2 rounded-full bg-[#240046] px-4 py-2 text-sm font-medium text-white hover:bg-purple-800"><Plus className="h-4 w-4" />New note</button>
    </div>

    {showForm && <form onSubmit={saveNote} className="rounded-xl border border-purple-100 bg-purple-50/50 p-4 space-y-3">
      <div className="grid gap-3 md:grid-cols-[180px_1fr]"><label className="text-sm font-medium text-gray-700">Date<input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2" /></label><label className="text-sm font-medium text-gray-700">Title<input maxLength="150" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Note title" className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2" /></label></div>
      <label className="block text-sm font-medium text-gray-700">Content<textarea maxLength="10000" required rows="5" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write the note..." className="mt-1 block w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2" /></label>
      <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">Cancel</button><button disabled={saving} className="rounded-lg bg-[#240046] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving...' : 'Save note'}</button></div>
    </form>}
    {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {loading ? <div className="py-10 text-center text-sm text-gray-500">Loading notes...</div> : notes.length === 0 ? <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center"><StickyNote className="mx-auto mb-2 h-8 w-8 text-gray-300" /><p className="text-sm font-medium text-gray-600">No notes yet</p><p className="text-xs text-gray-400">Create the first note for this {entityType}.</p></div> : <div className="space-y-3">{notes.map(note => <article key={note._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><h4 className="font-semibold text-gray-900">{note.title}</h4><div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500"><span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{new Date(note.date).toLocaleDateString()}</span><span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5" />{note.ownerName}</span></div></div><button onClick={() => deleteNote(note._id)} title="Delete note" className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div><p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">{note.content}</p></article>)}</div>}
  </div>
}
